import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import type { ApiResponse, AuthResponse, User, LoginRequest, RegisterRequest } from '../types/api';

class AuthService {
  private static TOKEN_KEY = 'access_token';
  private static REFRESH_KEY = 'refresh_token';
  private static USER_KEY = 'user';

  // Register new user
  async register(email: string, password: string, name: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REGISTER}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name } as RegisterRequest)
    });

    const data: ApiResponse<AuthResponse> = await response.json();

    console.log('Register response:', data); // Debug log

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Registration failed');
    }

    // Handle both possible response formats (camelCase preferred, as that's what backend uses)
    const responseData = data.data as any;
    const accessToken = responseData.accessToken || responseData.access_token;
    const refreshToken = responseData.refreshToken || responseData.refresh_token;

    console.log('Extracted tokens:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken
    });

    if (!accessToken || !refreshToken) {
      console.error('Tokens missing in response. Response data:', responseData);
      throw new Error('Registration response missing tokens');
    }

    // Store tokens and user
    this.setTokens(accessToken, refreshToken);
    this.setUser(data.data.user);
    console.log('Tokens stored successfully');

    return data.data.user;
  }

  // Login existing user
  async login(email: string, password: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password } as LoginRequest)
    });

    const data: ApiResponse<AuthResponse> = await response.json();

    console.log('Login response:', data); // Debug log

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Login failed');
    }

    // Handle both possible response formats (camelCase preferred, as that's what backend uses)
    const responseData = data.data as any;
    const accessToken = responseData.accessToken || responseData.access_token;
    const refreshToken = responseData.refreshToken || responseData.refresh_token;

    console.log('Extracted tokens:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenLength: accessToken?.length,
      refreshTokenLength: refreshToken?.length
    });

    if (!accessToken || !refreshToken) {
      console.error('Tokens missing in response. Response data:', responseData);
      throw new Error('Login response missing tokens');
    }

    // Store tokens and user
    console.log('About to store tokens...');
    this.setTokens(accessToken, refreshToken);
    this.setUser(data.data.user);
    console.log('Tokens stored! Check localStorage now.');

    return data.data.user;
  }

  // Logout
  async logout(): Promise<void> {
    try {
      const token = this.getAccessToken();
      if (token) {
        await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGOUT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } finally {
      this.clearAuth();
    }
  }

  // Get current user from API
  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ME}`, {
      headers: this.getAuthHeaders()
    });

    const data: ApiResponse<{ user: User }> = await response.json();

    if (!data.success || !data.data) {
      throw new Error(data.error || 'Failed to get user');
    }

    this.setUser(data.data.user);
    return data.data.user;
  }

  // Token management
  getAccessToken(): string | null {
    return localStorage.getItem(AuthService.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(AuthService.REFRESH_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(AuthService.TOKEN_KEY, accessToken);
    localStorage.setItem(AuthService.REFRESH_KEY, refreshToken);
  }

  // User management
  getUser(): User | null {
    const userStr = localStorage.getItem(AuthService.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  setUser(user: User): void {
    localStorage.setItem(AuthService.USER_KEY, JSON.stringify(user));
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getAccessToken() !== null;
  }

  // Clear all auth data
  clearAuth(): void {
    localStorage.removeItem(AuthService.TOKEN_KEY);
    localStorage.removeItem(AuthService.REFRESH_KEY);
    localStorage.removeItem(AuthService.USER_KEY);
  }

  // Get headers for authenticated requests
  getAuthHeaders(): HeadersInit {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }
}

export const authService = new AuthService();
